import { useEffect, useState, useCallback } from 'react';
import { getApiUrl, getVapidKey } from '../config/environment';

// Detectar iOS (iPhone/iPad/iPod)
export const isIOSDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Detectar se está rodando como PWA (modo standalone — obrigatório no iOS)
export const isPWAMode = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

// Converter chave VAPID de base64url para Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function buildApiUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

interface UsePushNotificationsOptions {
  enabled?: boolean;
  userId?: string | null;
  onPromptNeeded?: (requestPermission: () => void) => void;
  onIOSInstallNeeded?: () => void;
}

export const usePushNotifications = ({
  enabled = true,
  userId = null,
  onPromptNeeded,
  onIOSInstallNeeded,
}: UsePushNotificationsOptions = {}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [hasAskedThisSession, setHasAskedThisSession] = useState(false);

  const ios = isIOSDevice();
  const pwa = isPWAMode();

  // Resetar hasAskedThisSession quando o userId mudar (logout → re-login)
  // Isso garante que a subscription é re-sincronizada com o novo token
  useEffect(() => {
    setHasAskedThisSession(false);
    setIsRegistered(false);
  }, [userId]);

  useEffect(() => {
    // iOS requer modo PWA para web push
    if (ios && !pwa) {
      setIsSupported(false);
      return;
    }

    const supported =
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      // Verificar se já está inscrito
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => {
          if (sub) setIsRegistered(true);
        })
        .catch(() => {});
    }
  }, [ios, pwa]);

  const registerPushNotifications = useCallback(async () => {
    try {
      console.log('[PushNotif] Iniciando registro...');

      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        console.warn('[PushNotif] Permissão negada');
        localStorage.setItem('push_permission_asked', 'true');
        return;
      }

      // Padrao AFAS: garantir que o SW esta registrado e ativo antes de criar subscription
      let registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        console.log('[PushNotif] SW nao registrado, registrando agora...');
        registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
        await navigator.serviceWorker.ready;
      }

      if (!registration.active) {
        console.log('[PushNotif] Aguardando SW ativar...');
        await navigator.serviceWorker.ready;
        registration = await navigator.serviceWorker.getRegistration() ?? registration;
      }

      console.log('[PushNotif] Service Worker pronto:', registration.active?.state);

      const existingSubscription = await registration.pushManager.getSubscription();

      if (existingSubscription) {
        console.log('[PushNotif] Subscription existente, sincronizando com backend...');
        await sendSubscriptionToBackend(existingSubscription, ios);
        setIsRegistered(true);
        return;
      }

      const vapidKey = getVapidKey();
      if (!vapidKey) {
        console.error('[PushNotif] VAPID key não configurada');
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      console.log('[PushNotif] Subscription criada:', subscription.endpoint.substring(0, 50) + '...');

      await sendSubscriptionToBackend(subscription, ios);
      setIsRegistered(true);
    } catch (error) {
      console.error('[PushNotif] Erro ao registrar:', error);
    }
  }, [ios]);

  // Efeito principal: decidir quando e como solicitar permissão
  // userId muda ao logar/deslogar, o que dispara re-sincronização da subscription
  useEffect(() => {
    if (!enabled || !userId || hasAskedThisSession) return;

    // iOS fora de PWA: mostrar guia de instalação
    // Usa localStorage (não sessionStorage) para não perder ao matar o app no iOS
    if (ios && !pwa) {
      const alreadyShown = localStorage.getItem('ios_install_shown');
      if (!alreadyShown) {
        setHasAskedThisSession(true);
        setTimeout(() => {
          onIOSInstallNeeded?.();
          localStorage.setItem('ios_install_shown', 'true');
        }, 2500);
      }
      return;
    }

    if (!isSupported) return;

    // Já negou: não pedir de novo
    if (Notification.permission === 'denied') {
      setHasAskedThisSession(true);
      return;
    }

    // Já tem permissão: registrar/sincronizar direto (re-login também passa aqui)
    if (Notification.permission === 'granted') {
      // Delay curto: o token JWT às vezes é gravado logo após setar o user no store.
      // Sem isso, /notifications/subscribe falha e a conta (ex: ant2) fica sem Subscription.
      setHasAskedThisSession(true);
      setTimeout(() => {
        void registerPushNotifications();
      }, 800);
      return;
    }

    // Permissão default: mostrar modal iOS-friendly antes de pedir
    setHasAskedThisSession(true);
    const delay = ios ? 2000 : 1500;
    setTimeout(() => {
      if (onPromptNeeded) {
        onPromptNeeded(registerPushNotifications);
      } else {
        registerPushNotifications();
      }
    }, delay);
  }, [enabled, userId, isSupported, ios, pwa, hasAskedThisSession, registerPushNotifications]);

  // Re-sincroniza subscription ao voltar para foreground (evita perda após refresh de token/SW).
  useEffect(() => {
    if (!enabled || !userId || !isSupported) return;
    if (Notification.permission !== 'granted') return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        registerPushNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, userId, isSupported, registerPushNotifications]);

  return {
    isSupported,
    isRegistered,
    permission,
    isIOS: ios,
    isPWA: pwa,
    requestPermission: registerPushNotifications,
  };
};

async function sendSubscriptionToBackend(
  subscription: PushSubscription,
  isIOS: boolean,
): Promise<void> {
  try {
    const token = localStorage.getItem('@backoffice:token');
    if (!token) {
      console.error('[PushNotif] Token de autenticação ausente. Subscription não enviada ao backend.');
      return;
    }
    const apiUrl = getApiUrl();

    const subscribeUrl = buildApiUrl(apiUrl, '/notifications/subscribe');
    const response = await fetch(subscribeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        platform: isIOS ? 'ios' : 'web',
        userAgent: navigator.userAgent,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error(`[PushNotif] Backend retornou ${response.status}:`, text);
      return;
    }

    console.log('[PushNotif] ✅ Subscription registrada no backend com sucesso');
  } catch (error) {
    console.error('[PushNotif] Erro ao enviar subscription para backend:', error);
  }
}
